<template>
  <div class="wp-root">

    <!-- Header — same pattern as OnboardingLayout -->
    <header class="wp-header">
      <RouterLink to="/" class="wp-logo">
        <img src="/new-logo.png" class="wp-logo-mark" alt="">
        <span class="wp-logo-text">Maths Eleven Plus</span>
      </RouterLink>
    </header>

    <main class="wp-main">

      <!-- ── PAID PLAN ──────────────────────────────────────────── -->
      <div v-if="isPaid" class="wp-card">

        <!-- Hero: confetti + badge + headline -->
        <div class="wp-card-hero">
          <div class="wp-confetti-row">
            <span class="wp-confetti" style="animation-delay:0s">🎉</span>
            <span class="wp-confetti" style="animation-delay:0.15s">⭐</span>
            <span class="wp-confetti" style="animation-delay:0.3s">🎉</span>
          </div>
          <div class="wp-plan-badge">{{ planBadge }}</div>
          <h2 class="wp-h2">Welcome to {{ planDisplayName }}!</h2>
          <p class="wp-sub">Your plan is active. Here's everything now unlocked — and what's coming next.</p>
        </div>

        <div class="wp-rule"></div>

        <!-- Unlocked features -->
        <p class="wp-section-label">Everything unlocked on your plan</p>
        <div class="wp-perks">
          <div class="wp-perk">
            <span class="wp-perk-icon">🎯</span>
            <div>
              <div class="wp-perk-title">Skills Diagnostic</div>
              <div class="wp-perk-desc">Pinpoints exactly where your child should start across all 47 topics</div>
            </div>
          </div>
          <div class="wp-perk">
            <span class="wp-perk-icon">📚</span>
            <div>
              <div class="wp-perk-title">4,800+ Adaptive Questions</div>
              <div class="wp-perk-desc">Auto-adjust to your child's level across all topic areas</div>
            </div>
          </div>
          <div class="wp-perk">
            <span class="wp-perk-icon">🔄</span>
            <div>
              <div class="wp-perk-title">Spaced Revision</div>
              <div class="wp-perk-desc">Weak areas resurface automatically at the right intervals</div>
            </div>
          </div>
          <div class="wp-perk">
            <span class="wp-perk-icon">📊</span>
            <div>
              <div class="wp-perk-title">Progress Reports</div>
              <div class="wp-perk-desc">PDF exports, topic accuracy charts and streak tracking</div>
            </div>
          </div>
          <div class="wp-perk">
            <span class="wp-perk-icon">{{ isPlanFamily ? '👨‍👩‍👧‍👦' : '👨‍👩‍👧' }}</span>
            <div>
              <div class="wp-perk-title">{{ isPlanFamily ? 'Up to 3 children' : '1 child' }}</div>
              <div class="wp-perk-desc">Manage all your children from one parent dashboard</div>
            </div>
          </div>
        </div>

        <div class="wp-rule"></div>

        <!-- What happens next -->
        <p class="wp-section-label">What happens next</p>
        <div class="wp-steps">
          <template v-if="!isUpgrade">
            <div class="wp-step">
              <div class="wp-step-num">1</div>
              <div class="wp-step-body">
                <div class="wp-step-title">Child profile setup</div>
                <div class="wp-step-desc">Enter your child's name, pick an avatar and a learning theme that keeps them motivated.</div>
              </div>
            </div>
            <div class="wp-step-connector"></div>
          </template>
          <div class="wp-step">
            <div class="wp-step-num">{{ isUpgrade ? 1 : 2 }}</div>
            <div class="wp-step-body">
              <div class="wp-step-title">Skills Check — 15–20 minutes</div>
              <div class="wp-step-desc">A diagnostic across all topic areas. Nothing to pass or fail — it finds the perfect starting level.</div>
            </div>
          </div>
        </div>

        <!-- Footer CTA -->
        <div class="wp-footer">
          <button class="wp-btn" @click="proceed">Let's get started →</button>
        </div>

      </div>

      <!-- ── FREE PLAN ───────────────────────────────────────────── -->
      <div v-else class="wp-card">
        <div class="wp-card-hero">
          <div class="wp-confetti-row">
            <span class="wp-confetti" style="animation-delay:0s">🎉</span>
            <span class="wp-confetti" style="animation-delay:0.15s">⭐</span>
            <span class="wp-confetti" style="animation-delay:0.3s">🎉</span>
          </div>
          <h2 class="wp-h2">Welcome to Maths Eleven Plus</h2>
          <p class="wp-sub">Your free account is ready. Next we'll set up your child's profile — it only takes a minute.</p>
        </div>
        <div class="wp-footer">
          <button class="wp-btn" @click="proceed">Continue to child setup →</button>
          <p class="wp-footer-note">You can upgrade to Pro at any time from your dashboard</p>
        </div>
      </div>

    </main>

  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';

const router = useRouter();
const route  = useRoute();
const userStore = useUserStore();

const plan = computed(() => {
  const s = userStore.session;
  return s?.effectivePlan || s?.plan || 'free';
});

const isPaid        = computed(() => plan.value !== 'free');
const isPlanFamily  = computed(() => plan.value === 'family');
const isPlanProPlus = computed(() => plan.value === 'pro_plus');

const planDisplayName = computed(() =>
  ({ family: 'Family', pro_plus: 'Pro Plus', pro: 'Pro' }[plan.value] ?? 'Free')
);

const planBadge = computed(() => {
  if (isPlanFamily.value)  return '⭐ Family Plan';
  if (isPlanProPlus.value) return '💎 Pro Plus Plan';
  return '⭐ Pro Plan';
});
const isUpgrade = computed(() => route.query.upgrade === 'true');

function proceed() {
  const upgrade = route.query.upgrade === 'true';
  router.push(upgrade ? '/onboarding?upgrade=true' : '/onboarding');
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Jost:wght@300;400;500;600;700&display=swap');

/* ── Root — same background as OnboardingLayout ── */
.wp-root {
  min-height: 100vh;
  background: #0D1628;
  font-family: 'Jost', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-image:
    radial-gradient(circle at 80% 20%, rgba(184,144,42,0.06) 0%, transparent 50%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23B8902A' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
}

/* ── Header — same as ol-header ── */
.wp-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: rgba(13,22,40,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(184,144,42,0.15);
}

.wp-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}
.wp-logo-mark {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  flex-shrink: 0;
}
.wp-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 17px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.02em;
}

/* ── Main ── */
.wp-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 88px 24px 56px;
  width: 100%;
}

/* ── Card — same look as ob-card ── */
.wp-card {
  width: 100%;
  max-width: 540px;
  background: #F9F6EF;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(184,144,42,0.15);
  animation: wp-fadein 0.4s ease both;
}
@keyframes wp-fadein {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Card hero (top section) ── */
.wp-card-hero {
  text-align: center;
  margin-bottom: 24px;
}
.wp-confetti-row {
  font-size: 26px;
  margin-bottom: 14px;
  display: flex;
  justify-content: center;
  gap: 10px;
}
.wp-confetti {
  display: inline-block;
  animation: wp-bounce 0.5s ease both;
}
@keyframes wp-bounce {
  0%   { opacity: 0; transform: scale(0.5) translateY(-6px); }
  60%  { transform: scale(1.15) translateY(2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
.wp-plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, #B8902A, #d4a843);
  color: #0D1628;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 4px 16px;
  border-radius: 100px;
  margin-bottom: 14px;
}
.wp-h2 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 30px;
  font-weight: 600;
  color: #0D1628;
  line-height: 1.2;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.wp-sub {
  font-size: 15px;
  color: #555;
  font-weight: 400;
  line-height: 1.6;
  margin: 0;
}

/* ── Divider ── */
.wp-rule {
  height: 1px;
  background: #e8e4da;
  margin: 24px 0;
}

/* ── Section label ── */
.wp-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.wp-section-label::before {
  content: '';
  width: 18px;
  height: 1px;
  background: #B8902A;
  flex-shrink: 0;
}

/* ── Feature perks ── */
.wp-perks {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 4px;
}
.wp-perk {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 12px 14px;
  background: rgba(184,144,42,0.05);
  border: 1px solid rgba(184,144,42,0.14);
  border-radius: 6px;
}
.wp-perk-icon {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 1px;
}
.wp-perk-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 17px;
  font-weight: 600;
  color: #0D1628;
  margin-bottom: 2px;
  line-height: 1.2;
}
.wp-perk-desc {
  font-size: 13px;
  color: #666;
  font-weight: 400;
  line-height: 1.5;
}

/* ── Steps ── */
.wp-steps {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 4px;
}
.wp-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: rgba(255,255,255,0.6);
  border: 1px solid #e0dbd0;
  border-radius: 6px;
  padding: 16px 18px;
}
.wp-step-connector {
  width: 2px;
  height: 12px;
  background: rgba(184,144,42,0.3);
  margin-left: 25px;
}
.wp-step-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #B8902A, #d4a843);
  color: #0D1628;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: 'Cormorant Garamond', serif;
}
.wp-step-body { flex: 1; }
.wp-step-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-weight: 600;
  color: #0D1628;
  margin-bottom: 3px;
  line-height: 1.25;
}
.wp-step-desc {
  font-size: 14px;
  color: #555;
  font-weight: 400;
  line-height: 1.55;
}

/* ── Footer ── */
.wp-footer {
  border-top: 1px solid #e8e4da;
  margin-top: 24px;
  padding-top: 20px;
  text-align: center;
}
.wp-btn {
  display: block;
  width: 100%;
  background: #B8902A;
  color: #0D1628;
  border: none;
  border-radius: 4px;
  padding: 14px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Jost', sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
}
.wp-btn:hover {
  background: #d4a843;
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(184,144,42,0.25);
}
.wp-footer-note {
  margin-top: 12px;
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

@media (max-width: 600px) {
  .wp-card { padding: 28px 20px; }
  .wp-h2 { font-size: 26px; }
}
</style>
